import { useEffect, useState } from "react";
import clsx from "clsx";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Spinner } from "@/components/ui/Spinner";
import {
  fetchDokumentBeauftragungPdf,
  fetchDokumentKalkulationPdf,
  fetchDokumentPdf,
  fetchDokumentSignedPdf,
  mapPdfOpenVariantToPrintVariant,
  type DokumentPdfOpenVariant,
} from "@/lib/dokumente";
import { DokumentPrintAction } from "@/pages/dokumente/DokumentPrintAction";

export type DocumentPdfVariant = DokumentPdfOpenVariant | "signed";

/** Session-Cache: erneutes Öffnen desselben Dokuments ohne zweiten Netzwerk-Roundtrip. */
const pdfBlobCache = new Map<string, Blob>();

function cacheKey(docId: number, variant: DocumentPdfVariant): string {
  return `${docId}:${variant}`;
}

function fetchVariantPdf(docId: number, variant: DocumentPdfVariant): Promise<Blob> {
  if (variant === "kalkulation") return fetchDokumentKalkulationPdf(docId);
  if (variant === "beauftragung") return fetchDokumentBeauftragungPdf(docId);
  if (variant === "signed") {
    // Unterschriebenes PDF (Attachment/SharePoint); wenn es nicht ladbar ist,
    // lieber das erstellte PDF zeigen als eine leere Ansicht.
    return fetchDokumentSignedPdf(docId).catch(() => fetchDokumentPdf(docId));
  }
  return fetchDokumentPdf(docId);
}

function mediaType(blob: Blob): string {
  return (blob.type.split(";")[0] ?? "").trim().toLowerCase();
}

function asPdfBlob(blob: Blob): Blob {
  const type = mediaType(blob);
  if (!type || type === "application/octet-stream" || type === "application/pdf") {
    return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
  }
  return blob;
}

export function DocumentPdfEmbed({
  docId,
  variant = "angebot",
  className,
  showPrintAction = true,
  zIndexClass,
}: {
  docId: number;
  variant?: DocumentPdfVariant;
  className?: string;
  /** Drucken über App-Drucker (Standort-Dialog). */
  showPrintAction?: boolean;
  zIndexClass?: string;
}) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const key = cacheKey(docId, variant);
    const cached = pdfBlobCache.get(key);
    setError(null);

    if (cached) {
      setBlob(cached);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);

    void fetchVariantPdf(docId, variant)
      .then((raw) => {
        const next = asPdfBlob(raw);
        pdfBlobCache.set(key, next);
        if (!cancelled) setBlob(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setBlob(null);
        setError(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [docId, variant]);

  const title =
    variant === "kalkulation"
      ? "Kalkulations-PDF"
      : variant === "beauftragung"
        ? "Beauftragungsdokument"
        : variant === "signed"
          ? "Unterschriebenes PDF"
          : "Dokument PDF";

  const canPrint = showPrintAction && variant !== "signed";

  const showSpinner = loading && !blob;
  const showFile = Boolean(blob);
  const isImage = Boolean(blob && mediaType(blob).startsWith("image/"));

  return (
    <section
      className={clsx(
        "relative flex min-h-[min(72vh,56rem)] flex-col overflow-hidden rounded-xl border border-separator bg-fill-quaternary/15",
        className,
      )}
    >
      {canPrint && showFile && !isImage ? (
        <div className="flex items-center justify-end gap-2 border-b border-separator px-3 py-2">
          <DokumentPrintAction
            docId={docId}
            variant={mapPdfOpenVariantToPrintVariant(variant)}
            size="sm"
            zIndexClass={zIndexClass}
          />
        </div>
      ) : null}
      {showSpinner ? (
        <div className="flex min-h-[min(72vh,56rem)] flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : null}
      {error && !blob ? (
        <p className="flex min-h-[12rem] flex-1 items-center justify-center px-4 text-center text-sm text-status-red">
          {error}
        </p>
      ) : null}
      {blob && isImage ? <BlobImage blob={blob} title={title} /> : null}
      {blob && !isImage ? (
        <InlinePdfView blob={blob} title={title} className="min-h-[min(72vh,56rem)] w-full flex-1" />
      ) : null}
      {loading && blob ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-center pt-3">
          <span className="rounded-full bg-card/90 px-2.5 py-1 text-[11px] text-label-secondary shadow-sm ring-1 ring-separator/40">
            PDF wird aktualisiert …
          </span>
        </div>
      ) : null}
    </section>
  );
}

function BlobImage({ blob, title }: { blob: Blob; title: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  if (!url) return null;
  return (
    <div className="flex min-h-[min(40vh,24rem)] flex-1 items-center justify-center p-4">
      <img src={url} alt={title} className="max-h-[min(72vh,48rem)] max-w-full object-contain" />
    </div>
  );
}
